import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { notification } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { CurrentUser } from 'models/User'
import useKrpano from 'pages/building/panoramas/hooks/useKrpano'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import {
  buildingModeState,
  isRefetchingAnnotationsState,
  listPointState,
  pendingDisplayImageState,
  revalidateFileListState,
  revalidatePointDetailState,
  selectedAreaState,
  selectedPointDetail,
  selectedSubImageState
} from 'store/buildingStore'
import { displayToggleState } from 'store/displayToggleStore'
import { currentPovState } from 'store/krpanoStore'
import useSWR from 'swr'
import {
  AnnotationType,
  Point,
  SenceData,
  SenceDataResponse
} from 'types/building'
import Krpano from 'utils/krpano'

import BCPAnnotationModal from './components/BCPAnnotationModal/BCPAnnotationModal'
import DirectionSetting from './components/DirectionSetting'
import AnnotationModal from './components/ModalAddAnnotation'
import ModalAddStairway from './components/ModalAddStairway'
import TourAnnotationModal from './components/tourAnnotation/ModalAddAnnotation'
import usePreviewKrpano from './hooks/usePreviewKrpano'

export default function AreaPanorama() {
  const { id, floorId } = useParams()
  const { t } = useTranslation()
  const { getDirectory, getBuiildingType } = useDirectory()
  const [searchParams] = useSearchParams()

  const isFirstLoading = useRef(true)
  const urlH = searchParams.get('hlookat')
  const urlV = searchParams.get('vlookat')
  const urlZ = searchParams.get('fov')

  const [selectedPoint, setSelectedPoint] = useState('')

  const [buildingMode, setBuildingMode] = useRecoilState(buildingModeState)
  const [revalidatePointDetail, setRevalidatePointDetail] = useRecoilState(
    revalidatePointDetailState
  )
  const [isRefetchingAnnotations, setIsRefetchingAnnotations] = useRecoilState(
    isRefetchingAnnotationsState
  )
  const [, setDisplayToggle] = useRecoilState(displayToggleState)
  const [selectedArea, setSelectedArea] = useRecoilState(selectedAreaState)
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const points = useRecoilValue(listPointState)
  const [currentPov, setCurrentPov] = useRecoilState(currentPovState)
  const selectedSubImage = useRecoilValue(selectedSubImageState)
  const setSelectedSubImage = useSetRecoilState(selectedSubImageState)
  const [, setRevalidateFileList] = useRecoilState(revalidateFileListState)
  const [pendingDisplayImage] = useRecoilState(pendingDisplayImageState)

  const { trigger: getSenceData } = useMutation(`v1/points/file-info`)
  const { data: profile } = useSWR<CurrentUser | null>('v1/users/me', {
    revalidateOnMount: false
  })

  const { mutate: refetchPoints } = useSWR(
    {
      url: 'v1/points',
      params: {
        buildingId: id,
        floorId
      }
    },
    {
      revalidateOnMount: false
    }
  )

  const {
    embedpano: embedFull,
    currentSelectedPoint,
    currentSelectedAnnotation
  } = useKrpano({
    buildingMode: buildingMode.selectedMode
  })

  const handlePreloadSence = async () => {
    const embedpano = document.getElementById('embedpano-full') as any

    const listPointTo = points
      .find((item) => item.id === currentSelectedPoint)
      ?.directions?.map((item) => item.point_to)

    if (!listPointTo?.length) {
      return
    }

    await getSenceData(
      {
        method: 'post',
        data: {
          building_id: Number(id),
          floor_id: Number(floorId),
          point_ids: listPointTo
        }
      },
      {
        onSuccess: (data: SenceDataResponse) => {
          try {
            const currentFileId = points.find(
              (p) => p.id === currentSelectedPoint
            )?.image360?.id

            const items: SenceData[] = []
            data.data.forEach((item) => {
              if (
                embedpano.get(`scene[scene_${item.id}]`) === null &&
                item.id !== currentFileId
              ) {
                items.push(item)
              }
            })
            if (items.length > 0) {
              const screenString = Krpano.generateSceneXMLs(items)
              embedpano.call(
                `includexmlstring(${screenString}, null, null, false)`
              )
            }
          } catch (error) {}
        },
        onError: () => {}
      }
    )
  }

  const isVisibleFull =
    buildingMode.previewMode && Boolean(currentSelectedPoint)

  const { embedpano: embedPreview } = usePreviewKrpano()

  // get point detail when select point
  const { mutate } = useSWR<Point>(
    selectedPoint && points
      ? {
          url: `v1/points/${selectedPoint}`,
          params: {
            building_id: id,
            floor_id: floorId
          }
        }
      : null,
    {
      onSuccess: async (data) => {
        setPointDetail(data)
        if (data?.areaId !== selectedArea) {
          setSelectedArea(data?.areaId)
        }
        setRevalidatePointDetail(undefined)
        if (data?.image360) {
          const isLocked = data?.isLocked && data?.lockedBy === profile?.id
          const isRefetchList = points?.find(
            (item) =>
              item.id === data.id &&
              (item?.lockedBy !== data?.lockedBy ||
                item?.isLocked !== data?.isLocked)
          )

          // Only update annotations if we're refetching for annotation updates
          if (isRefetchingAnnotations) {
            const annotations = data?.annotations?.filter((item) => {
              const viewingImage = data?.subImages?.find(
                (sub) => sub.id === selectedSubImage
              )
                ? selectedSubImage
                : data?.image360?.id
              if (item.imageID) {
                return item.imageID === viewingImage
              }
              return viewingImage === data?.image360?.id
            })

            // Update only annotations without changing view
            const krpano = document.getElementById('embedpano-full') as any
            if (krpano) {
              // Remove old annotations and add new ones
              annotations.forEach((annotation) => {
                krpano.call(`remove_annotation(${annotation.id})`)
              })
              Krpano.AddAnnotation(
                krpano,
                annotations,
                null,
                getBuiildingType() === 10
              )
            }

            setIsRefetchingAnnotations(false)
          } else {
            // Normal refetch - update everything including view
            const selectedImageData =
              data.subImages?.find((img) => img.id === selectedSubImage) ||
              data.image360

            const isInternalNav =
              buildingMode.isClickToDirection ||
              buildingMode.selectedMode === 'direction'

            let initView

            if (isInternalNav && currentPov) {
              // [INHERIT] Continuity via arrow navigation
              initView = {
                ...data?.initView,
                h: currentPov.h,
                v: currentPov.v,
                z: currentPov.z
              }
            } else if (isFirstLoading.current && urlH && urlV) {
              // [URL] Shared link entry
              initView = {
                ...data?.initView,
                h: Number(urlH),
                v: Number(urlV),
                z: urlZ ? Number(urlZ) : 80
              }
            } else if (selectedImageData?.id !== data.image360?.id) {
              // Sub-image default initView
              initView = selectedImageData?.initView || data?.initView
            } else {
              // [RESET] Fresh load
              initView = data?.initView
            }

            isFirstLoading.current = false

            const initProps = {
              imageUrl: selectedImageData?.s3Path,
              previewUrl: selectedImageData?.multires
                ? `${selectedImageData?.tilesUrl}preview.jpg`
                : selectedImageData?.thumbnail,
              isLocked,
              hgap: data?.initView?.radarHGap,
              multires: selectedImageData?.multires,
              tilesUrl: selectedImageData?.tilesUrl,
              ...initView
            }
            const annotations = data?.annotations?.filter((item) => {
              const viewingImage = selectedImageData?.id || data?.image360?.id
              if (item.imageID) {
                return item.imageID === viewingImage
              }
              return viewingImage === data?.image360?.id
            })
            embedFull(initProps, annotations, data?.directions, data?.stairways)
            embedPreview(initProps)
            await handlePreloadSence()
          }

          if (isRefetchList) {
            refetchPoints()
          }

          setDisplayToggle({
            isLocked: data?.isLocked,
            lockedBy: data?.lockedBy
          })

          if (buildingMode.isClickToDirection) {
            setBuildingMode((prev) => ({
              ...prev,
              isClickToDirection: false
            }))
          }
        } else {
          Krpano.UnmountFull()
        }
      },
      onError: (err) => {
        if (err?.response?.data?.error?.key === 'point_not_exists_error') {
          refetchPoints()
          setRevalidateFileList(1)
          notification.error({
            message: t('translation.pointAlreadyDeleted')
          })
        }
      }
    }
  )

  // handle change preview when select subImage
  useEffect(() => {
    if (
      selectedSubImage &&
      pendingDisplayImage &&
      selectedSubImage === pendingDisplayImage.id
    ) {
      const initView = currentPov || pendingDisplayImage?.initView
      const initProps = {
        imageUrl: pendingDisplayImage?.s3Path,
        previewUrl: pendingDisplayImage?.multires
          ? `${pendingDisplayImage?.tilesUrl}preview.jpg`
          : pendingDisplayImage?.thumbnail,
        h: initView?.h || 0,
        v: initView?.v || 0,
        z: initView?.z || 0,
        hgap: pendingDisplayImage?.initView?.radarHGap || 0,
        multires: pendingDisplayImage?.multires,
        tilesUrl: pendingDisplayImage?.tilesUrl
      }
      embedPreview(initProps)
    } else if (pointDetail) {
      const selectedImageData = selectedSubImage
        ? pointDetail.subImages?.find((img) => img.id === selectedSubImage) ??
          pointDetail.image360
        : pointDetail.image360
      const initView =
        currentPov || selectedImageData?.initView || pointDetail.initView
      const initProps = {
        imageUrl: selectedImageData?.s3Path || '',
        previewUrl: selectedImageData?.multires
          ? `${selectedImageData?.tilesUrl}preview.jpg`
          : selectedImageData?.thumbnail || '',
        h: initView?.h || 0,
        v: initView?.v || 0,
        z: initView?.z || 0,
        hgap: pointDetail.initView?.radarHGap || 0,
        multires: selectedImageData?.multires,
        tilesUrl: selectedImageData?.tilesUrl
      }
      embedPreview(initProps)
    }
  }, [
    selectedSubImage,
    pendingDisplayImage,
    pointDetail,
    currentPov,
    embedPreview
  ])

  useEffect(() => {
    if (
      revalidatePointDetail &&
      currentSelectedPoint === revalidatePointDetail
    ) {
      mutate()
      return
    }
    if (revalidatePointDetail && selectedPoint && !currentSelectedPoint) {
      setSelectedPoint('')
      setPointDetail(undefined)
      setRevalidatePointDetail(undefined)
      Krpano.UnmountFull()
      return
    }
    setRevalidatePointDetail(undefined)
  }, [
    currentSelectedPoint,
    mutate,
    pointDetail?.id,
    revalidatePointDetail,
    selectedPoint,
    setPointDetail,
    setRevalidatePointDetail
  ])

  useEffect(() => {
    if (currentSelectedPoint) {
      setSelectedPoint(currentSelectedPoint)
      // Reset currentPov when point changes to prevent stale POV
      // from previous point being used in preview
      // not included when clicking on direction arrow button
      if (!buildingMode.isClickToDirection) {
        setCurrentPov(null)
      }
    }
  }, [buildingMode.isClickToDirection, currentSelectedPoint, setCurrentPov])

  useEffect(() => {
    return () => {
      setPointDetail(undefined)
      setSelectedSubImage(undefined)
    }
  }, [setPointDetail, setSelectedSubImage])

  const krpanoContentLayout =
    buildingMode.selectedMode === 'direction'
      ? 'absolute top-0 w-full bottom-[135px] bg-[#F9FBFF] border border-4 border-solid theme-border-sub-primary'
      : `absolute h-full w-full ${!isVisibleFull ? 'invisible' : ''}`

  const isNewTourAnnotation = useMemo(() => {
    return (
      currentSelectedAnnotation?.type === AnnotationType.INFO ||
      currentSelectedAnnotation?.type === AnnotationType.MOVIE ||
      currentSelectedAnnotation?.type === AnnotationType.IMAGE
    )
  }, [currentSelectedAnnotation])

  return (
    <>
      <div id="krpano-container" className={krpanoContentLayout} />

      {currentSelectedAnnotation &&
        getDirectory() !== 'bcp' &&
        (isNewTourAnnotation ? <TourAnnotationModal /> : <AnnotationModal />)}

      {currentSelectedAnnotation && getDirectory() === 'bcp' && (
        <BCPAnnotationModal />
      )}

      <ModalAddStairway />

      {buildingMode.selectedMode === 'direction' && (
        <DirectionSetting
          currentSelectedPoint={currentSelectedPoint}
          setBuildingMode={setBuildingMode}
        />
      )}
    </>
  )
}
